import { ApiProperty, ApiPropertyOptional } from '@nestjs/swagger';
import { Exclude, Expose, Transform, Type } from 'class-transformer';
import { CommonEntityDto } from '../../common/dto/common-entity.dto';
import { RepairInterface } from '../../repair/interfaces/repair.interface';
import {
  IsBoolean,
  IsNumber,
  IsOptional,
  IsString,
  Min,
  Max,
  MaxLength,
  IsUUID,
} from 'class-validator';
import { JobRepairInterface } from '../interfaces/job-repair.interface';
import { JobInterface } from '../../job/interfaces/job.interface';
import {
  PO_TOTAL_MAX,
  PO_TOTAL_MIN,
  PO_TOTAL_OVERRRIDE_MAX,
  PO_TOTAL_OVERRIDE_MIN,
} from '../constants/job-repair-validation.constants';
import { JobRepairPhotoDto } from '../../job-repair-photo/dto/job-repair-photo.dto';
import { RepairPaymentDto } from '../../repair-payment/dto/repair-payment.dto';
import { JobRepairNoteDto } from '../../job-repair-note/dto/job-repair-note.dto';
import { JobRepairBuilderSupervisorDto } from '../../job-repair-builder-supervisor/dto/job-repair-builder-supervisor.dto';
import { UserDto } from '../../user/dto/user.dto';

@Exclude()
export class JobRepairDto
  extends CommonEntityDto
  implements JobRepairInterface
{
  @ApiPropertyOptional({
    type: () => [RepairPaymentDto],
    title: 'Repair Payment DTO',
    description: 'Repair Payment DTO',
  })
  @Expose()
  repairPayment!: RepairPaymentDto[];

  @IsString()
  @MaxLength(1000)
  @ApiProperty({
    title: 'Job Repair Description',
  })
  @Expose()
  description!: string;

  @ApiProperty({
    title: 'Job Repair Date',
  })
  @Expose()
  repairDate!: Date;

  @ApiPropertyOptional({
    title: 'PO Total',
  })
  @Expose()
  @IsOptional()
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(PO_TOTAL_MIN)
  @Max(PO_TOTAL_MAX)
  poTotal!: number;

  @ApiProperty({
    title: 'PO Total Override',
  })
  @Expose()
  @IsNumber(
    { maxDecimalPlaces: 2 },
    {
      message: `PO Total Override must be a number with no more than 2 decimal places`,
    },
  )
  @Min(PO_TOTAL_OVERRIDE_MIN, {
    message: `PO Total Override must be greater than or equal to ${PO_TOTAL_OVERRIDE_MIN}`,
  })
  @Max(PO_TOTAL_OVERRRIDE_MAX, {
    message: `PO Total Override must be less than or equal to ${PO_TOTAL_OVERRRIDE_MAX}`,
  })
  @IsOptional()
  poTotalOverride!: number;

  @ApiProperty({
    title: 'Internal Invoice',
  })
  @Expose()
  @IsString()
  @MaxLength(100)
  internalInvoice!: string;

  @IsBoolean()
  @Type(() => Boolean)
  @ApiProperty({
    title: 'Ready To Book',
  })
  @Expose()
  readyToBook!: boolean;

  @ApiProperty({
    title: 'Non-Billable',
  })
  @IsBoolean()
  @Type(() => Boolean)
  @Expose()
  nonBillable!: boolean;

  @IsBoolean()
  @Type(() => Boolean)
  @ApiProperty({
    title: 'PO Released',
  })
  @Expose()
  poReleased!: boolean;

  @ApiProperty({
    title: 'Job ID',
  })
  @Expose()
  jobId!: string;

  @ApiProperty({
    title: 'Job',
  })
  @Expose()
  job?: JobInterface | undefined;

  @ApiProperty({
    title: 'Repair ID',
  })
  @Expose()
  repairId!: string;

  @ApiProperty({
    title: 'Repair',
  })
  @Expose()
  repair?: RepairInterface | undefined;

  @ApiProperty({
    format: 'uuid',
    description:
      'The initial supervisor user id at time of job repair creation.',
    nullable: true,
  })
  @IsUUID()
  @Expose()
  originalSupervisorUserId!: string;

  @ApiProperty({
    type: () => UserDto,
    description: 'The initial supervisor user at time of job repair creation.',
  })
  @Expose()
  originalSupervisorUser?: UserDto;

  @ApiPropertyOptional({
    type: () => [JobRepairPhotoDto],
    title: 'Job Repair Photos',
    description: 'The Job Repairs Photos associated with this job repair ',
  })
  @Expose()
  @Transform((value: JobRepairPhotoDto[]) => {
    if (value !== undefined && value.length > 0) {
      value = value.filter(
        (item: JobRepairPhotoDto) => item.deletedAt === null,
      );
    }
    return value;
  })
  jobRepairPhotos?: JobRepairPhotoDto[];

  @ApiPropertyOptional({
    type: () => [JobRepairNoteDto],
    title: 'Job Repair Notes',
    description: 'The Job Repairs Notes associated with this job repair ',
  })
  @Expose()
  jobRepairNotes?: JobRepairNoteDto[];

  @ApiPropertyOptional({
    type: () => [JobRepairBuilderSupervisorDto],
    title: 'Subdivision Builder Supervisors',
    description:
      'Subdivision builder supervisors associated with this job repair ',
  })
  @Expose()
  jobRepairSubdivisionBuilderSupervisors!: JobRepairBuilderSupervisorDto[];
}
